import { ComponentType, useMemo } from 'react'
import Markdown, { MarkdownToJSX } from 'markdown-to-jsx'
import { TableWrapper } from '@dc-shared-web/components/base/TableArtifact'

export type PageLinkComponent = ComponentType<{
  page: number
  children: React.ReactNode
  baseUrl?: string
}>
export type CitationComponent = ComponentType<{ id: number }>
export type WrapperComponent = ComponentType<{ children: React.ReactNode }>
export type FormatAiTextOptions = {
  formatLinksWitUrl?: {
    pathname: string
    searchParams: URLSearchParams
  }
}

type AIGeneratedTextProps = {
  text: string
  pageLink?: PageLinkComponent
  citation?: CitationComponent
  wrapper?: WrapperComponent
  className?: string
  displayPlainText?: boolean
  increasedPadding?: boolean
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
}

// Regular expression used to find mentions to pages in a chat response.
// Detects single references (e.g. "page 1") and multiple references separated by
// commas, ampersand, dashes and 'and' (e.g. "pages 1, 2 - 3 & 4 and 5").
// Avoids section references that start with page (e.g. "Page 8.2.7").
// In some cases, chat adds '.0' to the page number, so we need to account for that.
export const AI_TEXT_PAGE_RE =
  /pages?\s\d+(\.0)?(?!(\.\d+)+)(\s?[,&\-–—]?\s?(and)?\s?\d+(\.0)?(?!(\.\d+)+))*/gi

// Regular expression used to extract the links within a page reference.
// NOTE: This regex should only be used on strings that already matched AI_TEXT_PAGE_RE
const PAGE_LINK_EXTRACT_RE = /(pages?\s)?\d+(\.0)?/gi

const splitStringByRegexp = (value: string, re: RegExp) => {
  const pieces = []
  let currentPosition = 0
  let match

  while ((match = re.exec(value)) !== null) {
    if (currentPosition != match.index) {
      pieces.push({
        type: 'text',
        text: value.substring(currentPosition, match.index),
      })
    }

    if (match[0].trim() !== '') {
      pieces.push({
        type: 'match',
        text: match[0],
      })
    }

    currentPosition = match.index + match[0].length
  }

  if (currentPosition < value.length) {
    pieces.push({
      type: 'text',
      text: value.substring(currentPosition),
    })
  }

  return pieces
}

const createURL = (
  page: string,
  pathname: string,
  searchParams: URLSearchParams
) => {
  const newSearchParams = new URLSearchParams(searchParams)
  newSearchParams.set('page', page)
  return `${pathname}?${newSearchParams.toString()}`
}

const formatPageLinks = (
  piece: string,
  options: FormatAiTextOptions | undefined
) => {
  const matches = splitStringByRegexp(piece, PAGE_LINK_EXTRACT_RE)

  return matches.reduce((result, match) => {
    if (match.type === 'text') {
      result += match.text
    } else {
      const text = match.text.replace(/\.\d+/, '')
      const pageNum = text.replace(/[^\d]/g, '').trim()
      const linkHref = options?.formatLinksWitUrl
        ? createURL(
            pageNum,
            options.formatLinksWitUrl.pathname,
            options.formatLinksWitUrl.searchParams
          )
        : pageNum
      result += `[${text}](${linkHref})`
    }

    return result
  }, '')
}

export const formatAIText = (
  text: string | null | undefined,
  options?: FormatAiTextOptions
) => {
  const lines = (text ?? '').split('\n')

  return lines
    .map((line) =>
      splitStringByRegexp(line, AI_TEXT_PAGE_RE).reduce((result, piece) => {
        if (piece.type === 'match') {
          result += formatPageLinks(piece.text, options)
        } else {
          // Escape underscores between digits to prevent markdown from treating them as emphasis
          // Pattern: digit + underscore + digit (e.g., "01_80_13" becomes "01\_80\_13")
          result += piece.text.replace(/(\d)_(\d)/g, '$1\\_$2')
        }
        return result
      }, '')
    )
    .join('\n')
    .replace(/(^|\n) {4,}/gm, '$1  ') // Reduce 4+ spaces in markdown to 2 spaces to prevent code blocks
}

const AIText = ({
  text,
  pageLink: PageLink,
  wrapper,
  className,
  citation: Citation,
  displayPlainText = false,
  increasedPadding = false,
  onClick,
}: AIGeneratedTextProps) => {
  const formattedText = useMemo(() => formatAIText(text), [text])
  const overrides: MarkdownToJSX.Overrides = useMemo(() => {
    const paddingClass = increasedPadding ? 'tw-pl-4' : 'tw-pl-2'
    const CustomCodePreComponent = (
      props: React.HTMLAttributes<HTMLElement>
    ) => <p {...props} />

    return displayPlainText
      ? {
          p: { props: { className: 'tw-font-normal' } },
          strong: { props: { className: 'tw-font-normal' } },
          ol: { props: { className: 'tw-font-normal' } },
          ul: { props: { className: 'tw-font-normal' } },
          li: { props: { className: 'tw-font-normal' } },
          a: ({ children }) => <span className='tw-contents'>{children}</span>,
          citation: ({ id }) =>
            Citation ? <Citation id={parseInt(id)} /> : null,
          sources: () => null,
          h1: { props: { className: 'tw-font-normal' } },
          h2: { props: { className: 'tw-font-normal' } },
          h3: { props: { className: 'tw-font-normal' } },
          h4: { props: { className: 'tw-font-normal' } },
          h5: { props: { className: 'tw-font-normal' } },
          h6: { props: { className: 'tw-font-normal' } },
          code: CustomCodePreComponent,
          pre: CustomCodePreComponent,
          table: ({ children }) => <TableWrapper>{children}</TableWrapper>,
        }
      : {
          p: {
            props: {
              className:
                'tw-leading-[19.5px] [&>strong+strong]:tw-block [&>strong+strong]:tw-mt-1',
            },
          },
          strong: { props: { className: 'tw-font-medium' } },
          ol: {
            props: {
              className: `${paddingClass} [&>*]:tw-mb-3 last:[&>*]:tw-mb-0 [&>li]:tw-list-decimal [&>li>p]:tw-inline [&_ol]:tw-pl-0 [&_ol_li]:tw-ml-4`,
            },
          },
          ul: {
            props: {
              className: `${paddingClass} [&>li]:tw-list-disc [&>li>p]:tw-inline [&_ul]:tw-pl-0 [&_ul_li]:tw-ml-4`,
            },
          },
          li: {
            props: {
              className:
                'tw-leading-[19.5px] [&>ul]:tw-pl-2 [&>ol]:tw-pl-2 tw-mx-1',
            },
          },
          a: ({ href, children }) =>
            PageLink ? (
              <PageLink page={href}>{children}</PageLink>
            ) : (
              <span className='tw-contents'>{children}</span>
            ),
          citation: ({ id }) =>
            Citation ? <Citation id={parseInt(id)} /> : null,
          sources: () => null,
          h1: { props: { className: 'tw-text-xl tw-font-medium' } },
          h2: { props: { className: 'tw-text-lg tw-font-medium' } },
          h3: { props: { className: 'tw-text-base tw-font-medium' } },
          h4: { props: { className: 'tw-text-sm tw-font-medium' } },
          h5: { props: { className: 'tw-text-xs tw-font-medium' } },
          h6: { props: { className: 'tw-text-2xs tw-font-medium' } },
          code: CustomCodePreComponent,
          pre: CustomCodePreComponent,
          table: ({ children }) => <TableWrapper>{children}</TableWrapper>,
        }
  }, [PageLink, Citation, displayPlainText, increasedPadding])

  return (
    <div onClick={onClick} className={className}>
      <Markdown
        options={{
          overrides,
          wrapper,
          forceWrapper: true,
        }}
      >
        {formattedText}
      </Markdown>
    </div>
  )
}

export default AIText
